import type {CanvasImage,CanvasPlaceholder} from './canvas-types';
import type {ConversationProjectionEvent} from './conversation-event-projection';

const planningId = (id:string) => id.startsWith('planning-img-');
export function clearConceptPlanningPlaceholders(tiles:Record<string,CanvasPlaceholder>,owner:string){
 const ids=Object.keys(tiles).filter(id=>planningId(id)&&tiles[id].batchId===owner);
 if(!ids.length)return tiles;
 const next={...tiles};for(const id of ids)delete next[id];return next;
}

/** Replayed worker events have the same visible loading slots as the live SSE
 * path. The caller binds assistantMessageId from the owned scheduler receipt. */
export function projectDurableImagePlaceholders(input:{placeholders:Record<string,CanvasPlaceholder>;images:CanvasImage[];events:ConversationProjectionEvent[]}){
 let next=input.placeholders;
 const renderingOwners=new Set([
  ...Object.entries(next).filter(([id])=>!planningId(id)).map(([,tile])=>tile.batchId),
  ...input.events.filter(event=>['canvas_image_pending','canvas_image_ready'].includes(event.type)).map(event=>event.data?.assistantMessageId),
 ]);
 for(const event of input.events){
  const d=event.data??{};
  const owner=d.assistantMessageId??d.conceptsRun?.sourceRunMessageId;
  if(event.type==='agent_task_start'&&d.conceptsRun?.autoBuild===true&&d.conceptsRun.status==='generating'&&typeof owner==='string'&&d.conceptsRun.sourceRunMessageId===owner){
   const count=d.conceptsRun.count;
   if(!Number.isSafeInteger(count)||count<1||count>50||renderingOwners.has(owner))continue;
   const existing=Object.values(next).filter(tile=>tile.batchId===owner);
   if(existing.length)continue;
   const ratio=typeof d.conceptsRun.aspectRatio==='string'?d.conceptsRun.aspectRatio.split(':').map(Number):[1,1];
   const aspect=ratio.length===2&&ratio.every((n:number)=>Number.isFinite(n)&&n>0)?ratio[0]/ratio[1]:1;
   const width=Math.round(300*Math.min(aspect,1)),height=Math.round(300/Math.max(aspect,1));
   const bottom=Math.max(0,...input.images.filter(image=>image.x>-9999).map(image=>image.y+(image.displayHeight??300)),...Object.values(next).map(tile=>tile.y+tile.height));
   next={...next};
   for(let index=0;index<count;index++){
    const id=`planning-img-${owner}-${index}`;
    next[id]={taskId:id,batchId:owner,batchLabel:'Planning ads',slotIndex:index,x:60+(index%5)*340,y:bottom+120+Math.floor(index/5)*380,width,height,statusText:`Planning ad ${index+1} of ${count}…`};
   }
   continue;
  }
  if(event.type==='agent_task_complete'&&(d.success===false||d.conceptsRun?.status==='failed')&&typeof owner==='string'){
   next=clearConceptPlanningPlaceholders(next,owner);continue;
  }
  if(['canvas_image_pending','canvas_image_ready'].includes(event.type)&&typeof owner==='string')next=clearConceptPlanningPlaceholders(next,owner);
  if(event.type==='canvas_job_settled'&&typeof d.assistantMessageId==='string'){
   for(const [id,tile] of Object.entries(next)){
    if(tile.batchId!==d.assistantMessageId)continue;
    if(planningId(id)){next={...next};delete next[id];continue;}
    const clientId=id.slice('agent-img-'.length);
    if(input.images.some(image=>image.id===clientId)){next={...next};delete next[id];}
    else if(!tile.failed)next={...next,[id]:{...tile,failed:true,statusText:undefined,errorMsg:d.status==='cancelled'?'This image was cancelled.':d.error??'This image could not finish.'}};
   }
   continue;
  }
  if(typeof d.requestId!=='string'||!d.requestId||typeof d.assistantMessageId!=='string')continue;
  const id=`agent-img-${d.requestId}`,old=next[id];
  if(event.type==='canvas_image_ready'){
   if(old&&old.batchId===d.assistantMessageId){next={...next};delete next[id];}
   continue;
  }
  if(event.type==='canvas_image_pending'){
   if(old||input.images.some(image=>image.id===d.requestId))continue;
   const w=Number(d.width)||1080,h=Number(d.height)||1080,scale=Math.min(300/w,300/h,1);
   const width=Math.round(w*scale),height=Math.round(h*scale);
   const bottom=Math.max(0,...input.images.map(image=>image.y+(image.displayHeight??300)),...Object.values(next).map(tile=>tile.y+tile.height));
   next={...next,[id]:{taskId:id,batchId:d.assistantMessageId,workflowRunId:d.workflowRunId,
    x:Number.isFinite(d.x)?d.x:60,y:Number.isFinite(d.y)?d.y:bottom+120,width,height,genWidth:w,genHeight:h,
    slotIndex:d.conceptIndex,prompt:d.prompt,statusText:d.statusText??'Generating…'}};
  }else if(old&&old.batchId===d.assistantMessageId){
   if(event.type==='canvas_image_failed')next={...next,[id]:{...old,failed:true,errorMsg:d.error??d.reason??'This image could not finish.',failure:d.failure,statusText:undefined}};
   if(event.type==='canvas_image_progress'&&!old.failed)next={...next,[id]:{...old,...(typeof d.statusText==='string'?{statusText:d.statusText}:{}),...(typeof d.previewUrl==='string'&&d.previewUrl.startsWith('https://')?{previewUrl:d.previewUrl}:{})}};
  }
 }
 return next;
}
